import type { Pessoa } from '@sheafyield/dominio';
import { gravar, type Banco } from '@sheafyield/sync';
import { useEffect, useRef, useState } from 'react';
import type { DonoDasLinhas } from '../dados/dono.js';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { Folha } from '../ds/Folha.js';
import { GrupoDeChips } from '../ds/GrupoDeChips.js';
import { Toast } from '../ds/Toast.js';
import {
  gravarRascunhoDaFicha,
  lerRascunhoDaFicha,
  limparRascunhoDaFicha,
  lerFolhaAtivaDaFicha,
  marcarFolhaAtivaDaFicha,
  type CamposDaPessoa,
} from './rascunhoDaFicha.js';
import { ROTULO_DO_STATUS } from './rotulos.js';
import './pessoas.css';

type Props = {
  banco: Banco;
  dono: DonoDasLinhas;
  aberta: boolean;
  pessoa: Pessoa | null;
  aoFechar: () => void;
};
type CamposEditaveis = Pick<
  Pessoa,
  'nome' | 'endereco' | 'telefone' | 'idioma' | 'status' | 'tags'
>;
type Alteracao = {
  anterior: Pessoa | null;
  salva: Pessoa;
  campos: (keyof CamposEditaveis)[];
};
const mensagemDe = (causa: unknown) =>
  causa instanceof Error
    ? causa.message
    : 'Não deu para salvar. Tente novamente.';
const mesma = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

function FormularioPessoa({
  pessoa,
  identidade,
  aoSalvar,
  aoFechar,
}: {
  pessoa: Pessoa | null;
  identidade: Pick<Pessoa, 'id' | 'user_id'>;
  aoSalvar: (
    valores: CamposEditaveis,
    tocados: (keyof CamposEditaveis)[],
  ) => Promise<void>;
  aoFechar: () => void;
}) {
  const [rascunho, setRascunho] = useState(() => {
    const salvo = lerRascunhoDaFicha(identidade, 'pessoa');
    if (salvo?.tipo === 'pessoa') return salvo;
    const iniciais: CamposDaPessoa = {
      nome: pessoa?.nome ?? '',
      endereco: pessoa?.endereco ?? '',
      telefone: pessoa?.telefone ?? '',
      idioma: pessoa?.idioma ?? '',
      status: pessoa?.status ?? 'interessado',
      etiquetas: pessoa?.tags.join(', ') ?? '',
    };
    return {
      tipo: 'pessoa' as const,
      valores: iniciais,
      iniciais,
      tocados: [] as (keyof CamposDaPessoa)[],
    };
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emCurso = useRef(false);
  const formulario = useRef<HTMLFormElement>(null);
  useEffect(() => {
    marcarFolhaAtivaDaFicha(identidade, 'pessoa');
  }, [identidade]);
  function mudar<C extends keyof CamposDaPessoa>(
    campo: C,
    valor: CamposDaPessoa[C],
  ) {
    const proximo = {
      ...rascunho,
      valores: { ...rascunho.valores, [campo]: valor },
      tocados: Array.from(new Set([...rascunho.tocados, campo])),
    };
    setRascunho(proximo);
    gravarRascunhoDaFicha(identidade, proximo);
  }
  async function salvar() {
    if (emCurso.current) return;
    emCurso.current = true;
    setSalvando(true);
    setErro(null);
    try {
      const { etiquetas, ...valores } = rascunho.valores;
      await aoSalvar(
        {
          ...valores,
          nome: valores.nome.trim(),
          endereco: valores.endereco.trim(),
          tags: Array.from(
            new Set(
              etiquetas
                .split(/[,·]/u)
                .map((tag) => tag.trim())
                .filter(Boolean),
            ),
          ),
        },
        // Voltar ao valor da abertura cancela a edição daquele campo. A
        // base acompanha o rascunho: reler a ficha após a trava adotaria o
        // valor sincronizado como base e transformaria essa desistência em
        // uma ordem para sobrescrevê-lo.
        rascunho.tocados
          .filter(
            (campo) => rascunho.valores[campo] !== rascunho.iniciais[campo],
          )
          .map((campo) => (campo === 'etiquetas' ? 'tags' : campo)),
      );
      limparRascunhoDaFicha(identidade, 'pessoa');
    } catch (causa) {
      setErro(mensagemDe(causa));
    } finally {
      emCurso.current = false;
      setSalvando(false);
    }
  }
  return (
    <Folha
      aberta
      titulo={pessoa === null ? 'Nova pessoa' : 'Editar pessoa'}
      focoInicial={formulario}
      aoFechar={() => {
        if (emCurso.current) return false;
        return aoFechar();
      }}
    >
      <form
        ref={formulario}
        tabIndex={-1}
        className="ficha__formulario"
        onSubmit={(evento) => {
          evento.preventDefault();
          void salvar();
        }}
      >
        <Campo
          rotulo="Nome"
          value={rascunho.valores.nome}
          onChange={(e) => mudar('nome', e.target.value)}
          disabled={salvando}
        />
        <Campo
          rotulo="Endereço"
          value={rascunho.valores.endereco}
          onChange={(e) => mudar('endereco', e.target.value)}
          disabled={salvando}
        />
        <Campo
          rotulo="Telefone"
          type="tel"
          value={rascunho.valores.telefone}
          onChange={(e) => mudar('telefone', e.target.value)}
          disabled={salvando}
        />
        <Campo
          rotulo="Idioma"
          value={rascunho.valores.idioma}
          onChange={(e) => mudar('idioma', e.target.value)}
          disabled={salvando}
        />
        <fieldset disabled={salvando} className="pessoa-formulario__estado">
          <GrupoDeChips
            rotulo="Estado"
            nome="estado-da-pessoa"
            valor={rascunho.valores.status}
            aoMudar={(valor) => mudar('status', valor)}
            opcoes={(Object.keys(ROTULO_DO_STATUS) as Pessoa['status'][]).map(
              (valor) => ({ valor, rotulo: ROTULO_DO_STATUS[valor] }),
            )}
          />
        </fieldset>
        <Campo
          rotulo="Etiquetas"
          dica="Separe as etiquetas por vírgulas."
          value={rascunho.valores.etiquetas}
          onChange={(e) => mudar('etiquetas', e.target.value)}
          disabled={salvando}
        />
        {erro === null ? null : <p role="alert">{erro}</p>}
        <Botao type="submit" largura="cheia" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar'}
        </Botao>
      </form>
    </Folha>
  );
}

/** O invólucro permanece montado quando a folha fecha: Desfazer pertence à
 * tela. Só o formulário remonta, por abertura/conta/id, nunca pelo updated_at. */
export function FolhaPessoa({ banco, dono, aberta, pessoa, aoFechar }: Props) {
  const [conta, setConta] = useState<{ id: string; user_id: string } | null>(
    null,
  );
  const [retomada, setRetomada] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [aviso, setAviso] = useState<Alteracao | null>(null);
  const desfazendo = useRef(false);
  // O prazo vale só até a primeira tentativa. Limpar a mensagem de erro
  // para editar outro campo não pode tirar a segunda chance de Desfazer.
  const [avisoTentado, setAvisoTentado] = useState<typeof aviso>(null);
  const id = pessoa?.id ?? 'nova';
  const donoDaPessoa = pessoa?.user_id;
  const [origem, setOrigem] = useState({ id, donoDaPessoa, dono, tentativa });
  if (
    origem.id !== id ||
    origem.donoDaPessoa !== donoDaPessoa ||
    origem.dono !== dono ||
    origem.tentativa !== tentativa
  ) {
    setOrigem({ id, donoDaPessoa, dono, tentativa });
    setConta(null);
    setRetomada(false);
    setErro(null);
    setAviso(null);
    setAvisoTentado(null);
  }
  useEffect(() => {
    let ativa = true;
    void dono()
      .then((user_id) => {
        if (!ativa) return;
        if (donoDaPessoa !== undefined && user_id !== donoDaPessoa)
          throw new Error('Esta ficha pertence a outra conta.');
        const identidade = { id, user_id };
        setConta(identidade);
        setRetomada(lerFolhaAtivaDaFicha(identidade) === 'pessoa');
      })
      .catch((causa: unknown) => {
        if (ativa) setErro(mensagemDe(causa));
      });
    return () => {
      ativa = false;
    };
  }, [dono, id, donoDaPessoa, tentativa]);
  function fechar() {
    if (conta !== null) marcarFolhaAtivaDaFicha(conta, null);
    setRetomada(false);
    aoFechar();
  }
  async function salvar(
    valores: CamposEditaveis,
    tocados: (keyof CamposEditaveis)[],
  ) {
    const user_id = await dono();
    if (conta === null || user_id !== conta.user_id)
      throw new Error('A conta mudou. Abra o formulário novamente.');
    const alteracao = await banco.transaction(
      'rw',
      banco.pessoa,
      banco.fila,
      async () => {
        const anterior =
          pessoa === null ? null : await banco.pessoa.get(pessoa.id);
        if (anterior === undefined || anterior?.deleted)
          throw new Error('Esta ficha não está mais disponível.');
        if (anterior !== null && anterior.user_id !== user_id)
          throw new Error('Esta ficha pertence a outra conta.');
        // A prop é o retrato da abertura. Reler e aplicar apenas campos tocados
        // conserva contatos sincronizados e os campos que esta folha não edita.
        const campos =
          anterior === null
            ? (Object.keys(valores) as (keyof CamposEditaveis)[])
            : tocados.filter(
                (campo) => !mesma(anterior[campo], valores[campo]),
              );
        const alterados = Object.fromEntries(
          campos.map((campo) => [campo, valores[campo]]),
        );
        const entrada =
          anterior === null
            ? {
                id: crypto.randomUUID(),
                user_id,
                ...valores,
                apelido: '',
                nota: '',
                lat: null,
                lng: null,
                casa_id: null,
                proxima_visita_em: null,
              }
            : { ...anterior, ...alterados };
        if (entrada.nome.trim() === '' && entrada.endereco.trim() === '')
          throw new Error(
            'Preencha um nome ou um endereço: é por um dos dois que a ficha é encontrada depois.',
          );
        // Salvar sem mudança apenas fecha: não cria um carimbo de sync
        // nem promete um Desfazer que não tem campo algum para restaurar.
        if (anterior !== null && campos.length === 0) return null;
        const salva = await gravar(banco, 'pessoa', entrada);
        return { anterior, salva, campos };
      },
    );
    setAviso(alteracao);
    setAvisoTentado(null);
    setErro(null);
    fechar();
  }
  async function desfazer() {
    if (aviso === null || desfazendo.current) return;
    desfazendo.current = true;
    setAvisoTentado(aviso);
    try {
      const user_id = await dono();
      if (user_id !== aviso.salva.user_id)
        throw new Error('A conta mudou. Não foi possível desfazer.');
      await banco.transaction('rw', banco.pessoa, banco.fila, async () => {
        const atual = await banco.pessoa.get(aviso.salva.id);
        if (atual === undefined || atual.deleted)
          throw new Error('Esta ficha não está mais disponível.');
        if (atual.user_id !== user_id)
          throw new Error('Esta ficha pertence a outra conta.');
        if (aviso.anterior === null) {
          if (atual.updated_at !== aviso.salva.updated_at)
            throw new Error(
              'A ficha foi alterada depois de salvar. Não foi possível desfazer a criação.',
            );
          await gravar(banco, 'pessoa', { ...atual, deleted: true });
        } else {
          // Desfazer restaura somente o que continua com nosso valor. Uma
          // atualização posterior do mesmo campo não pode ser apagada.
          const campos = aviso.campos.filter((campo) =>
            mesma(atual[campo], aviso.salva[campo]),
          );
          if (campos.length === 0)
            throw new Error(
              'A ficha foi alterada depois de salvar. Não foi possível desfazer.',
            );
          const anterior = aviso.anterior;
          const candidata = {
            ...atual,
            ...Object.fromEntries(
              campos.map((campo) => [campo, anterior[campo]]),
            ),
          };
          // Uma restauração parcial pode combinar dois vazios que nunca
          // coexistiram. A validação vale para a mesclagem, não só para
          // os retratos anterior e atual, que eram válidos separadamente.
          if (candidata.nome.trim() === '' && candidata.endereco.trim() === '')
            throw new Error(
              'A ficha mudou depois de salvar. Não foi possível desfazer: é preciso manter um nome ou um endereço.',
            );
          await gravar(banco, 'pessoa', candidata);
        }
      });
      setAviso(null);
      setAvisoTentado(null);
      setErro(null);
    } catch (causa) {
      setErro(mensagemDe(causa));
    } finally {
      desfazendo.current = false;
    }
  }
  return (
    <>
      {(aberta || retomada) && conta !== null ? (
        <FormularioPessoa
          key={`${conta.user_id}:${conta.id}`}
          pessoa={pessoa}
          identidade={conta}
          aoSalvar={salvar}
          aoFechar={fechar}
        />
      ) : null}
      {aberta && conta === null && erro === null ? (
        <p role="status">Abrindo formulário…</p>
      ) : null}
      {erro === null || (!aberta && !retomada && aviso === null) ? null : (
        <div>
          <p role="alert">{erro}</p>
          {conta === null ? (
            <Botao
              variante="secundario"
              onClick={() => setTentativa((n) => n + 1)}
            >
              Tentar novamente
            </Botao>
          ) : null}
        </div>
      )}
      <Toast
        chave={aviso}
        segundos={aviso !== null && avisoTentado === aviso ? 0 : 6}
        mensagem={aviso === null ? null : 'Pessoa salva'}
        {...(aviso === null
          ? {}
          : {
              acao: {
                rotulo: 'Desfazer',
                aoAcionar: () => {
                  void desfazer();
                },
              },
            })}
        aoDispensar={() => {
          setAviso(null);
          setAvisoTentado(null);
          setErro(null);
        }}
      />
    </>
  );
}
